import { colors, hexToRgba, icons, intl } from '@beelday/common'
import { keyframes } from '@emotion/react'
import styled from '@emotion/styled'
import {
	KeyboardEvent as ReactKeyboardEvent,
	ReactNode,
	useEffect,
	useRef,
} from 'react'
import { AgendaIcon } from 'room/common/presentation/icon/agenda'
import { ChatIcon } from 'room/common/presentation/icon/chat-icon'
import { WallIcon } from 'room/common/presentation/icon/wall-icon'
import { PHONE_TOP_BAR_HEIGHT } from 'room/common/presentation/in-person-phone'
import { FeedType } from 'session-info/training'
import { HamburgerIcon } from './illustrations'

const ellipsis = `
	white-space: nowrap;
	overflow: hidden;
	text-overflow: ellipsis;
`

const Bar = styled.header`
	flex-shrink: 0;
	display: flex;
	align-items: center;
	gap: 12px;
	min-height: ${PHONE_TOP_BAR_HEIGHT}px;
	padding: 8px 16px;
	border-bottom: 1px solid ${colors.borderLightGray};
	background: ${colors.white};
`

/** The Beelday colour wheel from the mockup's top bar; decoration only. */
const Badge = styled.span`
	width: 26px;
	height: 26px;
	flex-shrink: 0;
	border-radius: 50%;
	border: 2px solid ${colors.white};
	box-shadow: 0 0 0 1px ${colors.borderLightGray};
	background: conic-gradient(
		${colors.greenishCyan} 25%,
		${colors.lightishPurple} 25% 50%,
		${colors.gold} 50% 75%,
		${colors.blueGroup} 75%
	);
`

const Titles = styled.div`
	flex: 1;
	min-width: 0;
`

const Training = styled.div`
	font-size: 14px;
	font-weight: 700;
	color: ${colors.blackish};
	${ellipsis}
`

const Module = styled.div`
	font-size: 12px;
	color: ${colors.heather};
	${ellipsis}
`

const pulse = keyframes`
	0%, 100% { opacity: 1; }
	50% { opacity: .35; }
`

const Live = styled.div`
	flex-shrink: 0;
	display: flex;
	align-items: center;
	gap: 4px;
	font-size: 12px;
	font-weight: 700;
	color: ${colors.greenishTeal};
`

/** The stream is down: grey and still, until the room's state is back. */
const Reconnecting = styled(Live)`
	color: ${colors.warmGrey};
	font-weight: 600;
`

const ReconnectingDot = styled.span`
	width: 8px;
	height: 8px;
	border-radius: 50%;
	background: ${colors.borderGray};
`

const LiveDot = styled.span`
	width: 8px;
	height: 8px;
	border-radius: 50%;
	background: ${colors.greenishCyan};
	animation: ${pulse} 1.6s ease-in-out infinite;

	@media (prefers-reduced-motion: reduce) {
		animation: none;
	}
`

const RoundButton = styled.button`
	position: relative;
	width: 44px;
	height: 44px;
	flex-shrink: 0;
	display: flex;
	align-items: center;
	justify-content: center;
	border: none;
	border-radius: 50%;
	background: ${colors.athensGray};
	color: ${colors.indigoBlue};
	cursor: pointer;
`

const Dot = styled.span`
	position: absolute;
	top: 4px;
	right: 4px;
	width: 12px;
	height: 12px;
	border-radius: 50%;
	border: 2px solid ${colors.white};
	background: ${colors.lipstick};
`

type TopBarProps = {
	training?: string
	module?: string
	live: boolean
	/** The room's event stream is down and coming back: the phone may be behind. */
	reconnecting?: boolean
	hasUnread: boolean
	menuOpen: boolean
	menuButtonRef: React.RefObject<HTMLButtonElement>
	onMenu: () => void
}

/** The one piece of chrome that never changes: where am I, and the menu. */
export const TopBar = ({
	training,
	module,
	live,
	reconnecting = false,
	hasUnread,
	menuOpen,
	menuButtonRef,
	onMenu,
}: TopBarProps): JSX.Element => {
	const translate = intl.useTranslate()
	return (
		<Bar>
			<Badge aria-hidden />
			<Titles>
				<Training>{training}</Training>
				{module ? <Module>{module}</Module> : null}
			</Titles>
			{live && reconnecting ? (
				<Reconnecting role="status" data-testid="in_person.mobile.reconnecting">
					<ReconnectingDot aria-hidden />
					<intl.Translate>in_person.mobile.reconnecting</intl.Translate>
				</Reconnecting>
			) : live ? (
				<Live data-testid="in_person.mobile.live">
					<LiveDot aria-hidden />
					<intl.Translate>in_person.mobile.live</intl.Translate>
				</Live>
			) : null}
			<RoundButton
				ref={menuButtonRef}
				type="button"
				aria-label={translate(
					hasUnread ? 'in_person.mobile.menu.unread' : 'in_person.mobile.menu'
				)}
				aria-haspopup="menu"
				aria-expanded={menuOpen}
				onClick={onMenu}
				data-testid="in_person.mobile.menu"
			>
				<HamburgerIcon />
				{hasUnread ? <Dot aria-hidden /> : null}
			</RoundButton>
		</Bar>
	)
}

const Backdrop = styled.div`
	position: absolute;
	inset: 0;
	z-index: 60;
	background: ${hexToRgba(colors.blackish, 0.35)};
`

const Sheet = styled.div`
	position: absolute;
	top: 8px;
	right: 12px;
	z-index: 61;
	width: 240px;
	max-width: calc(100% - 24px);
	overflow: hidden;
	border-radius: 16px;
	background: ${colors.white};
	box-shadow: 0 12px 40px ${hexToRgba(colors.blackish, 0.22)};
`

const Row = styled.button<{ $danger?: boolean }>`
	display: flex;
	align-items: center;
	gap: 12px;
	width: 100%;
	min-height: 52px;
	padding: 12px 16px;
	border: none;
	background: none;
	font-family: inherit;
	font-size: 14px;
	font-weight: 500;
	text-align: left;
	color: ${props => (props.$danger ? colors.lipstick : colors.blackish)};
	cursor: pointer;

	& + & {
		border-top: 1px solid ${colors.borderLightGray};
	}

	&:focus-visible {
		outline: 2px solid ${colors.indigoBlue};
		outline-offset: -2px;
	}
`

const RowIcon = styled.span<{ $danger?: boolean }>`
	width: 24px;
	height: 24px;
	flex-shrink: 0;
	display: flex;
	align-items: center;
	justify-content: center;
	color: ${props => (props.$danger ? colors.lipstick : colors.indigoBlue)};
	fill: currentColor;

	svg {
		max-width: 22px;
		max-height: 22px;
	}
`

const Count = styled.span`
	min-width: 20px;
	height: 20px;
	margin-left: auto;
	padding: 0 4px;
	display: flex;
	align-items: center;
	justify-content: center;
	border-radius: 12px;
	background: ${colors.lipstick};
	color: ${colors.white};
	font-size: 12px;
	font-weight: 700;
`

const formatCount = (count: number): string =>
	count > 99 ? '99+' : String(count)

type MenuItemProps = {
	icon: ReactNode
	label: string
	count?: number
	danger?: boolean
	onClick: () => void
}

const MenuItem = ({
	icon,
	label,
	count = 0,
	danger,
	onClick,
}: MenuItemProps): JSX.Element => (
	<Row type="button" role="menuitem" $danger={danger} onClick={onClick}>
		<RowIcon $danger={danger} aria-hidden>
			{icon}
		</RowIcon>
		<intl.Translate>{label}</intl.Translate>
		{count > 0 ? <Count>{formatCount(count)}</Count> : null}
	</Row>
)

export type MenuTarget = 'chat' | 'agenda' | 'feed' | 'exit'

type MenuSheetProps = {
	feedType?: FeedType
	unread: { chat: number; feed: number }
	/** Where focus goes back to when the menu closes. */
	returnFocusTo: React.RefObject<HTMLElement>
	onPick: (target: MenuTarget) => void
	onClose: () => void
}

/**
 * The hamburger's contents: everything that is not the current activity.
 *
 * A menu, not a dialog: focus moves to the first item on open, arrows and
 * Tab cycle inside it, Escape or the backdrop close it, and focus returns to
 * the hamburger. The backdrop starts below the top bar, so the hamburger
 * itself still toggles it closed.
 */
export const MenuSheet = ({
	feedType,
	unread,
	returnFocusTo,
	onPick,
	onClose,
}: MenuSheetProps): JSX.Element => {
	const sheetRef = useRef<HTMLDivElement>(null)

	useEffect(() => {
		const items =
			sheetRef.current?.querySelectorAll<HTMLElement>('[role="menuitem"]')
		items?.[0]?.focus()
		const trigger = returnFocusTo.current
		return () => trigger?.focus()
	}, [returnFocusTo])

	const onKeyDown = (event: ReactKeyboardEvent<HTMLDivElement>) => {
		if (event.key === 'Escape') {
			event.preventDefault()
			onClose()
			return
		}
		const forward =
			event.key === 'ArrowDown' || (event.key === 'Tab' && !event.shiftKey)
		const backward =
			event.key === 'ArrowUp' || (event.key === 'Tab' && event.shiftKey)
		if (!forward && !backward) {
			return
		}
		const items = Array.from(
			sheetRef.current?.querySelectorAll<HTMLElement>('[role="menuitem"]') ?? []
		)
		if (!items.length) {
			return
		}
		event.preventDefault()
		const current = items.indexOf(document.activeElement as HTMLElement)
		const next = forward
			? (current + 1) % items.length
			: (current - 1 + items.length) % items.length
		items[next].focus()
	}

	return (
		<>
			<Backdrop onClick={onClose} aria-hidden />
			<Sheet
				ref={sheetRef}
				role="menu"
				onKeyDown={onKeyDown}
				data-testid="in_person.mobile.menu_sheet"
			>
				<MenuItem
					icon={<ChatIcon />}
					label="lobby.misc.messages"
					count={unread.chat}
					onClick={() => onPick('chat')}
				/>
				{feedType ? (
					<MenuItem
						icon={feedType === 'WALL' ? <WallIcon /> : <icons.BoardletIcon />}
						label={`lobby.misc.${feedType}`}
						count={unread.feed}
						onClick={() => onPick('feed')}
					/>
				) : null}
				<MenuItem
					icon={<AgendaIcon />}
					label="lobby.misc.agenda"
					onClick={() => onPick('agenda')}
				/>
				<MenuItem
					icon={<icons.ExitIcon />}
					label="in_person.mobile.exit"
					danger
					onClick={() => onPick('exit')}
				/>
			</Sheet>
		</>
	)
}

const Fab = styled.button`
	position: absolute;
	right: 16px;
	bottom: 20px;
	z-index: 40;
	width: 56px;
	height: 56px;
	display: flex;
	align-items: center;
	justify-content: center;
	border: 1px solid ${colors.borderLightGray};
	border-radius: 50%;
	background: ${colors.white};
	box-shadow: 0 6px 20px ${hexToRgba(colors.blackish, 0.16)};
	color: ${colors.indigoBlue};
	fill: currentColor;
	cursor: pointer;

	svg {
		max-width: 22px;
		max-height: 22px;
	}
`

const FabCount = styled.span`
	position: absolute;
	top: -4px;
	right: -4px;
	min-width: 20px;
	height: 20px;
	padding: 0 4px;
	display: flex;
	align-items: center;
	justify-content: center;
	border: 2px solid ${colors.white};
	border-radius: 12px;
	background: ${colors.lipstick};
	color: ${colors.white};
	font-size: 12px;
	font-weight: 700;
`

type FeedButtonProps = {
	feedType: FeedType
	count: number
	onClick: () => void
}

/** The boardlet, one tap away from any state, with what is new in it. */
export const FeedButton = ({
	feedType,
	count,
	onClick,
}: FeedButtonProps): JSX.Element => {
	const translate = intl.useTranslate()
	return (
		<Fab
			type="button"
			aria-label={
				count > 0
					? translate('in_person.mobile.feed.unread', {
							feed: translate(`lobby.misc.${feedType}`),
							count,
					  })
					: translate(`lobby.misc.${feedType}`)
			}
			onClick={onClick}
			data-testid="in_person.mobile.feed_button"
		>
			{feedType === 'WALL' ? <WallIcon /> : <icons.BoardletIcon />}
			{count > 0 ? <FabCount aria-hidden>{formatCount(count)}</FabCount> : null}
		</Fab>
	)
}

const PanelContainer = styled.div`
	position: absolute;
	inset: 0;
	z-index: 30;
	display: flex;
	flex-direction: column;
	padding: 16px 16px 0;
	background: ${colors.athensGray};
`

const PanelHead = styled.div`
	display: flex;
	align-items: center;
	gap: 8px;
	margin-bottom: 16px;
`

const Segments = styled.div`
	flex: 1;
	min-width: 0;
	display: flex;
	padding: 4px;
	border-radius: 12px;
	background: ${colors.inactiveGray};
`

const Segment = styled.button<{ $on: boolean }>`
	flex: 1;
	min-width: 0;
	min-height: 36px;
	padding: 8px 4px;
	border: none;
	border-radius: 8px;
	background: ${props => (props.$on ? colors.white : 'transparent')};
	color: ${props => (props.$on ? colors.indigoBlue : colors.heather)};
	font-family: inherit;
	font-size: 12px;
	font-weight: 700;
	cursor: pointer;
	${ellipsis}
`

const Close = styled.button`
	width: 44px;
	height: 44px;
	flex-shrink: 0;
	display: flex;
	align-items: center;
	justify-content: center;
	border: none;
	border-radius: 50%;
	background: ${colors.white};
	color: ${colors.blackish};
	cursor: pointer;

	svg {
		width: 14px;
		height: 14px;
	}
`

/**
 * The chat's message bar spans the panel like the chat header above it and
 * sits on the panel's grey, not on a white strip of its own (see `Composer` in
 * messaging's `message-input.tsx`). Grey rather than transparent: the bar is
 * opaque so the message list's shadow does not show through as a band.
 */
const PanelContent = styled.div`
	flex: 1;
	min-height: 0;
	display: flex;
	flex-direction: column;
	overflow: auto;
	--beelday-chat-composer-padding: 12px 0 16px;
	--beelday-chat-composer-background: ${colors.athensGray};
`

export type PanelTab<T extends string | number> = { id: T; label: string }

type PanelProps<T extends string | number> = {
	tabs: PanelTab<T>[]
	selected: T
	onSelect: (id: T) => void
	onClose: () => void
	children: ReactNode
}

/**
 * Chat, agenda and boardlet as one panel over the stage. Over, not instead:
 * the stage stays mounted underneath, so a half-typed answer survives a look
 * at the chat.
 */
export const PanelLayout = <T extends string | number>({
	tabs,
	selected,
	onSelect,
	onClose,
	children,
}: PanelProps<T>): JSX.Element => {
	const translate = intl.useTranslate()
	return (
		<PanelContainer data-testid="in_person.mobile.panel">
			<PanelHead>
				<Segments role="tablist">
					{tabs.map(tab => (
						<Segment
							key={tab.id}
							type="button"
							role="tab"
							aria-selected={selected === tab.id}
							$on={selected === tab.id}
							onClick={() => onSelect(tab.id)}
						>
							<intl.Translate>{tab.label}</intl.Translate>
						</Segment>
					))}
				</Segments>
				<Close
					type="button"
					aria-label={translate('in_person.mobile.close')}
					onClick={onClose}
				>
					<icons.CloseIcon />
				</Close>
			</PanelHead>
			<PanelContent role="tabpanel">{children}</PanelContent>
		</PanelContainer>
	)
}
